/* Recherche : ouverture du dialogue et resultats en direct (Predictive Search).
   Aucune dependance. Le rendu passe par textContent : aucune injection HTML possible. */
(() => {
  const dialog = document.querySelector('[data-search-dialog]');
  if (!dialog) return;

  const input = dialog.querySelector('[data-search-input]');
  const results = dialog.querySelector('[data-search-results]');
  const idle = dialog.querySelector('[data-search-idle]');
  const status = dialog.querySelector('[data-search-status]');
  const { predictiveUrl, searchUrl, locale, currency } = dialog.dataset;

  const DEBOUNCE_MS = 200;
  const MIN_CHARS = 2;
  const LIMIT = 6;

  let money;
  try { money = new Intl.NumberFormat(locale || 'fr', { style: 'currency', currency }); } catch { money = null; }
  const formatPrice = (v) => (money ? money.format(parseFloat(v)) : v);

  const cache = new Map();
  let timer;
  let controller;

  // ---------- Petit utilitaire DOM ----------
  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  };

  // ---------- Ouverture / fermeture ----------
  const open = () => {
    if (!dialog.open) dialog.showModal();
    input.focus();
    input.select();
    // Le carrousel a ete initialise dans un dialogue ferme (largeur 0) : on resynchronise les fleches.
    window.dispatchEvent(new Event('resize'));
  };
  const close = () => dialog.open && dialog.close();

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-search]')) {
      e.preventDefault();
      open();
    }
  });
  dialog.querySelector('[data-search-close]').addEventListener('click', close);
  // Un clic sur le fond cible le <dialog> lui-meme (le panneau interieur remplit l'espace utile).
  dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
  // Echap est gere nativement par <dialog>.

  // ---------- Etats d'affichage ----------
  // Le carrousel des incontournables vit dans `idle` : masque, il perd sa mesure et aucun evenement
  // `scroll` ne previent ses fleches au reaffichage, d'ou la resynchronisation.
  const revealIdle = () => {
    if (!idle.hidden) return;
    idle.hidden = false;
    window.dispatchEvent(new Event('resize'));
  };

  const showIdle = () => {
    results.hidden = true;
    results.replaceChildren();
    revealIdle();
    status.textContent = '';
  };

  const fullSearchLink = (q) => {
    const wrap = el('div', 'sd__all');
    const a = el('a', 'btn btn-ghost', `Voir tous les résultats pour « ${q} »`);
    a.href = `${searchUrl}?q=${encodeURIComponent(q)}`;
    a.style.whiteSpace = 'normal';
    wrap.append(a);
    return wrap;
  };

  const productRow = (p) => {
    const a = el('a', 'sd__item');
    a.href = p.url;
    const thumb = el('span', 'media sd__thumb');
    const src = p.featured_image?.url || p.image;
    if (src) {
      const img = document.createElement('img');
      img.src = src + (src.includes('?') ? '&' : '?') + 'width=160';
      img.alt = '';
      img.loading = 'lazy';
      thumb.append(img);
    }
    const text = el('span');
    text.append(el('span', 'product-name', p.title));
    text.append(document.createElement('br'));
    text.append(el('span', 'product-price', p.price ? formatPrice(p.price) : ''));
    a.append(thumb, text);
    return a;
  };

  const chipLinks = (items, title) => {
    const group = el('section', 'sd__group');
    group.append(el('p', 'meta', title));
    const list = el('div', 'sd__links');
    items.forEach((it) => {
      const a = el('a', 'chip', it.title);
      a.href = it.url;
      list.append(a);
    });
    group.append(list);
    return group;
  };

  const render = (data, q) => {
    const { products = [], collections = [], pages = [] } = data.resources?.results || {};
    results.replaceChildren();

    if (!products.length && !collections.length && !pages.length) {
      // Aucun resultat : message + on garde populaires et incontournables visibles.
      const box = el('div', 'sd__empty');
      box.append(el('p', 'sd__empty-title', `Aucun résultat pour « ${q} »`));
      box.append(el('p', 'meta', 'Vérifiez l’orthographe, essayez un mot plus court ou explorez nos idées ci-dessous.'));
      results.append(box);
      revealIdle();
      status.textContent = `Aucun résultat pour ${q}`;
    } else {
      if (products.length) {
        const group = el('section', 'sd__group');
        group.append(el('p', 'meta', 'Bijoux'));
        const list = el('div', 'sd__list sd__list--products');
        products.forEach((p) => list.append(productRow(p)));
        group.append(list);
        results.append(group);
      }
      if (collections.length) results.append(chipLinks(collections, 'Collections'));
      if (pages.length) results.append(chipLinks(pages, 'Pages'));
      results.append(fullSearchLink(q));
      idle.hidden = true;
      status.textContent = `${products.length + collections.length + pages.length} suggestions`;
    }
    results.hidden = false;
  };

  const renderError = (q) => {
    results.replaceChildren();
    const box = el('div', 'sd__empty');
    box.append(el('p', 'sd__empty-title', 'La recherche rapide est indisponible'));
    box.append(el('p', 'meta', 'Appuyez sur Entrée pour lancer la recherche complète.'));
    results.append(box, fullSearchLink(q));
    results.hidden = false;
    revealIdle();
  };

  // ---------- Requete ----------
  const search = async (q) => {
    if (cache.has(q)) return render(cache.get(q), q);
    controller?.abort();
    controller = new AbortController();
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,collection,page',
      'resources[limit]': LIMIT,
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const res = await fetch(`${predictiveUrl}.json?${params}`, { signal: controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      cache.set(q, data);
      render(data, q);
    } catch (err) {
      if (err.name !== 'AbortError') renderError(q);
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < MIN_CHARS) {
      controller?.abort();
      showIdle();
      return;
    }
    timer = setTimeout(() => search(q), DEBOUNCE_MS);
  });

  // A la fermeture, on annule toute requete en cours.
  dialog.addEventListener('close', () => {
    clearTimeout(timer);
    controller?.abort();
  });
})();
